import { useSearchParams, useNavigate } from 'react-router-dom';
import React, { useEffect , useState } from "react";
import styled from "styled-components";
import { Box, List, ListItem, Button, Divider, Link } from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import Skeleton from '@mui/material/Skeleton';

import BootstrapInput from "../lib/BootstrapInput";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import AppDelegate from "../AppDelegate";
import Formatter from "../lib/Formatter";


const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const getDisplayFilename = (filename = "") => filename.split("/").pop() || filename;

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
};


const Download = () => {
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('cid');
  const token = searchParams.get('t');

  const [authenticated, setAuthenticated]  = useState(false);
  const [validLink, setValidLink]  = useState(false);
  const [pin, setPin]  = useState([]);
  const [files, setFiles]  = useState([]);
  const [downloadFiles, setDownloadFiles] = useState([]);
  const [isCheckingLink, setIsCheckingLink] = useState(true);
  const [hasRequestedPin, setHasRequestedPin] = useState(false);
  const [isRequestingPin, setIsRequestingPin] = useState(false);
  const [isSubmittingPin, setIsSubmittingPin] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [pinError, setPinError] = useState("");

  const [resendCounter, setResendCounter] = useState(0);

  useEffect(() => {
    if (resendCounter <= 0) return;

    const timer = setInterval(() => {
      setResendCounter((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer); // cleanup
  }, [resendCounter]);

  useEffect(() => {
    const validateDownloadLink = async () => {
      if (!caseId || !token) {
        navigate("/error");
        return;
      }

      setIsCheckingLink(true);

      try {
        const response = await AppDelegate.reqCheckDownloadLink({
          caseId: caseId,
          token: token,
        });

        if (response.code === 200) {
          setValidLink(true);
          setMaskedEmail(response.payload.email);
          setDownloadFiles(response.payload.files || []);
        } else if (response.code === 1302) {
          navigate("/error");
        } else {
          navigate("/error");
        }
      } catch (e) {
        navigate("/error");
      } finally {
        setIsCheckingLink(false);
      }
    };

    validateDownloadLink();
  }, [caseId, token, navigate]);

  const requestDownloadPin = async () => {
    setIsRequestingPin(true);
    try {
      const response = await AppDelegate.reqRequestDownloadPin({
        caseId: caseId,
        token: token,
      });

      if (response.code === 200 || response.code === 1301) {
        // 1301 means the old pin has not expired
        setValidLink(true);
        setMaskedEmail(response.payload.email || maskedEmail);
        setHasRequestedPin(true);
        setResendCounter(60);
      } else {
        navigate("/error");
      }
    } catch (e) {
      navigate("/error");
    } finally {
      setIsRequestingPin(false);
    }
  };

  const requestFileLink = async () => {
    if (pin.length <= 0) {
      setPinError("Please enter a valid PIN");
      return;
    }

    setIsSubmittingPin(true);
    const response = await AppDelegate.reqGetDownloadLink({
      caseId: caseId,
      token: token,
      pin: pin,
    });

    setIsSubmittingPin(false);
    if (response.code === 200) {
      setAuthenticated(true);
      setFiles(response.payload.files);
      setExpiryDate(Formatter.formatDateTime(response.payload.expiredAt));

    } else if (response.code === 802) {
      setPinError("Invalid PIN");

    } else {
      navigate("/error");
    }
  };

  const onClickDownload = (url) => {
    window.open(url, '_blank');
  };

  const onClickResendPin = () => {
    requestDownloadPin();
  };

  const onClickRequestPin = () => {
    requestDownloadPin();
  };

  const onClickEnterPin = (e) => {
    e.preventDefault();
    requestFileLink();
  };

  const downloadFileNames = downloadFiles
    .map((file) => getDisplayFilename(file.filename))
    .filter(Boolean)
    .join(", ");

  return (
    <Container>
      <Topbar />
        <Box sx={{
          width: '100%',
          height: '100%',
          backgroundColor: 'var(--light-gray)',
        }}>
          <Box sx={{
            padding: "100px 50px",
            maxWidth: 800,
            margin: "0 auto",
            textAlign: 'center',
          }}>
            {isCheckingLink &&
              <Box sx={{
                mt: 6,
                maxWidth: '700px',
              }}>
                <Skeleton variant="rounded" width={700} height={400} />
              </Box>
            }

            {(!isCheckingLink && validLink && !authenticated && !hasRequestedPin) &&
              <Box>
                <h1 style={{
                  fontSize: "2rem",
                }}>Request your download PIN</h1>
                <p>
                  {downloadFileNames
                    ? `This secure download includes ${downloadFileNames}. `
                    : ""}
                  Request a PIN to be sent to {maskedEmail} in order to download the file.
                </p>
                <Box sx={{
                  mt: 5,
                }}>
                  <Button
                    variant="contained"
                    onClick={onClickRequestPin}
                    disabled={isRequestingPin}
                    sx={{
                      backgroundColor: '#1a3a52',
                      borderRadius: 0,
                      boxShadow: 'none',
                      fontFamily: 'inherit',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      px: 2,
                      py: 1.1,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#2d5573',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isRequestingPin ? "Requesting..." : "Request PIN"}
                  </Button>
                </Box>
              </Box>
            }

            {(!isCheckingLink && validLink && !authenticated && hasRequestedPin) &&
              <>
                <Box>
                  <h1 style={{
                    fontSize: "2rem",
                  }}>Please enter the PIN</h1>
                  <p>We have just sent a PIN to your contact email: {maskedEmail}</p>
                  <p>The PIN is valid for 10 minutes</p>
                </Box>
                <Box sx={{
                  m: 2,
                  mt: 5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                }}>

                  <BootstrapInput
                    id="pin-textfield"
                    label="PIN"
                    variant="outlined"
                    value={pin}
                    size="small"
                    disabled={isSubmittingPin}
                    error={pinError}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {;
                        e.preventDefault();
                        onClickEnterPin(e);
                      }
                    }}
                    onChange={(e) => {
                      if (e.target.value.length <= 6) {
                        setPin(e.target.value);
                    }}}
                  />
                  <Button
                    variant="contained"
                    type='submit'
                    onClick={onClickEnterPin}
                    sx={{
                      marginLeft: 2,
                      backgroundColor: '#1a3a52',
                      borderRadius: 0,
                      boxShadow: 'none',
                      fontFamily: 'inherit',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      px: 2,
                      py: 1.1,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#2d5573',
                        boxShadow: 'none',
                      },
                    }}
                    disabled={isSubmittingPin}
                    loading={isSubmittingPin}
                  >
                    Submit
                  </Button>
                </Box>

                <Box>
                  {resendCounter > 0 &&
                    <Box>
                      You can resend the PIN in {resendCounter} seconds.
                    </Box>
                  }
                  {resendCounter <= 0 &&
                    <Link
                      onClick={onClickResendPin}
                      disabled={isSubmittingPin || (resendCounter > 0)}
                    >
                      Resend PIN
                    </Link>
                  }
                </Box>

              </>
            }

            {(validLink && authenticated ) &&
              <>
                <Box>
                  <h1 style={{
                    fontSize: "2rem",
                  }}>Download Your Report File Here</h1>
                  <p>This link will expire in {expiryDate}</p>
                </Box>
                <Box sx={{
                  backgroundColor: '#fff',
                  p: 4,
                  borderRadius: 2,
                  maxWidth: 700,
                  mx: 'auto',
                  mt: 6,
                  boxShadow: 3,
                  textAlign: 'center',
                }}>
                  {files && (
                    <List>
                      {files.map((file, index) => (
                        <React.Fragment key={file.filename}>
                          <ListItem
                            sx={{
                              alignItems: { xs: 'stretch', sm: 'center' },
                              display: 'grid',
                              gap: 2,
                              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                              px: 0,
                              py: 2,
                            }}
                          >
                            <Box sx={{
                              minWidth: 0,
                              textAlign: 'left',
                            }}>
                              <Box sx={{
                                color: '#1a3a52',
                                fontWeight: 700,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                              }}>
                                {getDisplayFilename(file.filename)}
                              </Box>
                              <Box sx={{
                                color: '#64748b',
                                fontSize: '0.92rem',
                                mt: 0.5,
                              }}>
                                {formatFileSize(file.size) || "Size unavailable"}
                                {file.description ? ` - ${file.description}` : ""}
                              </Box>
                            </Box>
                            <Button
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              onClick={() => onClickDownload(file.url)}
                              disabled={!file.url}
                              sx={{
                                alignSelf: { xs: 'stretch', sm: 'center' },
                                backgroundColor: '#1a3a52',
                                borderRadius: 0,
                                boxShadow: 'none',
                                fontFamily: 'inherit',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                justifySelf: { xs: 'stretch', sm: 'end' },
                                minWidth: 132,
                                px: 2,
                                py: 1.1,
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: '#2d5573',
                                  boxShadow: 'none',
                                },
                              }}
                            >
                              Download
                            </Button>
                          </ListItem>
                          {index < files.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              </>
            }
          </Box>
        </Box>
      <Footer/>
    </Container>
  );
};

export default Download;
